// EnterEmailComponent.js
import React, { useEffect, useState, useRef } from "react";
import { useAtom } from "jotai";
import {
  emailAtom,
  emailValidAtom,
  emailCheckWelcomeNameAtom,
  registeredUserIdAtom,
  messageForLoginModalAtom,
  onboardingScreenAtom,
  onboardingCurrentScreenAtom,
  onboardingCompletedAtom,
} from "@/store/atoms";
import LoginWithButton from "@/components/common/LoginWithButton";

import {
  useEmailCheck,
  useI18N,
  useRegisterSocialDesktop,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";

import Loader from "@/components/common/Loader";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useGoogleLogin } from "@react-oauth/google";
import FacebookLoginButton from "@/components/Buttons/FacebookLoginButton";
import MicrosoftLoginButton from "@/components/Buttons/MicrosoftLoginButton";
import {
  appleLoginClientId,
  facebookLoginClientId,
  appleLoginDesktopRedirectUri,
  microsoftLoginClientId,
  microsoftLoginTenantId,
  microsoftLoginRedirectUri,
  showMicrosoftLogin,
} from "common/config";
import { trackEvent, createPageAnalytics } from "common/analytics";
import { applyUserIdentity, signupDateToday } from "@/analytics/identity";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import FadingMessage from "@/components/common/FadingMessage";

import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import WideButton from "@/components/common/WideButton";
import { useCustomToast } from "@/hooks/useCustomToast";
import { XIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { StandardButton } from "@/components/common/StandardButton";
import { entryPageFromCurrentPage } from "@/analytics/entryPage";

interface EnterEmailProps {
  onClose: () => void;
  title: string;
}

const EnterEmailComponent = ({ onClose, title }: EnterEmailProps) => {
  const i18n = useI18N();
  const { modalStyle, modalClassName } = useModalHeight();
  const isZoomedIn = useZoomDetection();
  const isDark = useDarkMode();

  const showCustomToast = useCustomToast();

  const [email, setEmail] = useAtom(emailAtom);
  const [, setEmailValid] = useAtom(emailValidAtom);
  const [, setEmailCheckWelcomeName] = useAtom(emailCheckWelcomeNameAtom);
  const [, setRegisteredUserId] = useAtom(registeredUserIdAtom);
  const [mesageForLoginModal, setMesageForLoginModal] = useAtom(
    messageForLoginModalAtom,
  );
  const [, setVisible] = useState(true); // State for visibility

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { setScreenIndex } = useAuthModals();
  const authLandingAnalytics = createPageAnalytics("auth_landing");

  const { checkEmail, isLoading, error, result } = useEmailCheck();

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus
  const emailRef = useRef<HTMLInputElement>(null);

  const [, setIsOnboardingModalOpen] = useAtom(onboardingScreenAtom);
  const [, setOnboardingCurrentScreen] = useAtom(onboardingCurrentScreenAtom);
  const [, setIsOnboardingCompleted] = useAtom(onboardingCompletedAtom);

  const handleOpenOnboardingSliderModal = () => {
    console.log("in EPC handleOpenOnboardingSliderModal");
    setOnboardingCurrentScreen(0);
    setIsOnboardingModalOpen(true);
    setIsOnboardingCompleted(false);
  };

  useEffect(() => {
    authLandingAnalytics.track("page_view_page", {
      entry_point: "auth_landing",
      entry_page: entryPageFromCurrentPage(),
    }); // auth_landing_viewed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  const {
    registerSocialDesktop,
    error: errorSocial,
    isLoading: isLoadingSocial,
    data,
  } = useRegisterSocialDesktop();

  // Provider attempted on the social path, so the errorSocial handler can attribute
  // account_login_failed to the right auth_provider.
  const attemptedSocialProviderRef = useRef<string | null>(null);

  useEffect(() => {
    if (data) {
      console.log("Login done. data=", data);
      console.log("Login done. data.firstLogin=", data?.data?.firstLogin);
      const socialLoginStatus = data?.data?.social_login_status;
      const userId = data?.data?.id;
      if (socialLoginStatus == "register") {
        setRegisteredUserId(userId);

        // New centralized identity (additive). The backend auto-verifies social signups (it stamps
        // email_verification at user creation), so the email is verified the moment the account exists.
        applyUserIdentity(
          { id: userId, email },
          { email_verified: true, account_creation_date: signupDateToday() },
        );

        // show onBording
        setScreenIndex(AuthModalScreen.OnboardingStep1);
      } else {
        // New centralized identity (additive). Social accounts are verified at creation, so an
        // existing-user social login is always a verified email.
        applyUserIdentity({ id: userId, email }, { email_verified: true });

        if (data?.data?.firstLogin == 1) {
          //call open onboarding slider
          handleOpenOnboardingSliderModal();
        }

        setScreenIndex(AuthModalScreen.EnterEmail);
        onClose();
      }
    }
  }, [data, email, onClose, setRegisteredUserId, setScreenIndex]);

  const handleClose = () => {
    authLandingAnalytics.track("close_action_button", {
      auth_screen: "auth_entry",
    });
    onClose();
  };

  useEffect(() => {
    if (errorSocial) {
      console.log("errorSocial", errorSocial);
      // setMesageForLoginModal(errorSocial?.message);
      showCustomToast(errorSocial?.message, "error");
      // account_login_failed — the backend rejected the social token, most commonly because the
      // email is registered with a different method (auth_method_mismatch); transport failures map
      // to network_error. auth_provider = the provider the user attempted.
      const message = (errorSocial?.message ?? "").toLowerCase();
      const errorType =
        !message || message.includes("fetch") || message.includes("network")
          ? "network_error"
          : "auth_method_mismatch";
      if (attemptedSocialProviderRef.current) {
        trackEvent("account_login_failed", {
          auth_provider: attemptedSocialProviderRef.current,
          error_type: errorType,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorSocial]);

  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      authLandingAnalytics.track("continue_with_google_action_button", {
        auth_provider: "google",
      });

      // You can now use the tokenResponse to authenticate the user in your app
      const access_token = tokenResponse.code;
      // console.log("Google login access_token", access_token);

      attemptedSocialProviderRef.current = "google";
      registerSocialDesktop({
        provider: "google",
        authToken: access_token,
      });
    },
    onError: () => {
      console.error("Google login failed");
      // Handle login errors here
    },
    flow: "auth-code", // Use'auth-code' for the authorization code flow
  });

  const handleFacebookResponse = (response) => {
    // console.log("Facebook login response:", response);
    // console.log("Facebook login accessToken:", response.accessToken);

    if (response.accessToken) {
      authLandingAnalytics.track("continue_with_facebook_action_button", {
        auth_provider: "facebook",
      });

      // console.log("Facebook login send backend request:");

      attemptedSocialProviderRef.current = "facebook";
      registerSocialDesktop({
        provider: "facebook",
        authToken: response.accessToken,
      });
    } else {
      //show alert message login failed
    }
  };

  const handleAppleResponse = (response) => {
    console.log("Apple login response:", response);

    authLandingAnalytics.track("continue_with_apple_action_button", {
      auth_provider: "apple",
    });

    attemptedSocialProviderRef.current = "apple";
    registerSocialDesktop({
      provider: "apple",
      authToken: response.authorization.id_token,
    });
  };

  const handleMicrosoftResponse = (response: { code?: string }) => {
    if (response.code) {
      attemptedSocialProviderRef.current = "microsoft";
      registerSocialDesktop({
        provider: "azure",
        authToken: response.code,
      });
    }
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const AppleID = (window as any).AppleID;
    if (AppleID) {
      AppleID.auth.init({
        clientId: appleLoginClientId,
        scope: "name email",
        redirectURI: appleLoginDesktopRedirectUri,
        responseType: "code id_token",
        responseMode: "form_post",
        usePopup: true,
      });
    }
  }, []);

  const handleAppleLogin = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await (window as any).AppleID.auth.signIn();
      handleAppleResponse(response);
    } catch (err) {
      console.error("Apple login failed", err);
    }
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailValid(validateEmail(e.target.value));
  };

  const handleCheckEmailContinue = (email: string) => {
    if (validateEmail(email)) {
      // console.log("email is valid send API call");
      checkEmail(email);
    } else {
      emailRef.current?.focus();
      setErrorMessage("Invalid email address");
    }
  };

  useEffect(() => {
    if (result) {
      if (result.social_status == "success") {
        // Email is registered with a social provider (auth_method_mismatch) —
        // generated tracking plan: account_login_failed on auth_landing. Guarded
        // because auth_provider is required; backend returns it lowercased.
        if (result.provider) {
          trackEvent("account_login_failed", {
            auth_provider: result?.provider?.toLowerCase(),
            error_type: "auth_method_mismatch",
          });
        }
        setErrorMessage(result?.message);
        emailRef.current?.focus();
      } else if (result.status === "error") {
        // Branch-point outcome: email not found -> create account path.
        trackEvent("auth_email_submitted", { auth_result: "create_account" });
        console.log("Status Error");

        setScreenIndex(AuthModalScreen.Register);
        setErrorMessage(result?.message);
      } else {
        // Branch-point outcome: email exists -> log in path.
        trackEvent("auth_email_submitted", { auth_result: "log_in" });
        setEmailCheckWelcomeName(result?.data?.first_name);
        setScreenIndex(AuthModalScreen.EnterPassword);
      }
    }
  }, [result, setScreenIndex, setEmailCheckWelcomeName]);

  useEffect(() => {
    if (error !== null) {
      console.log("error", error);
      setEmailCheckWelcomeName("");
      console.log("500 Error");
      setErrorMessage(error?.message);
      // Rejection lands asynchronously, so there is no submit branch to focus from.
      emailRef.current?.focus();
    }
  }, [error]);

  useEffect(() => {
    if (mesageForLoginModal !== "") {
      setVisible(true); // Set visibility to true when message is set
      setTimeout(() => {
        setVisible(false); // Set visibility to false to start fade out
      }, 2000); // Adjust the duration as needed
      setTimeout(() => {
        setMesageForLoginModal(""); // Clear the message after fade out
      }, 5000); // Adjust the duration to match the fade out time
    }
  }, [mesageForLoginModal, setMesageForLoginModal]);

  return (
    <div style={modalStyle} className={cn(modalClassName)}>
      <header
        className={`py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
      >
        <StandardButton
          onClick={handleClose}
          variant="closeModal"
          icon={
            <XIcon color={isDark ? "#A39FE8" : "#352FC3"} aria-hidden="true" />
          }
          aria-label="Close modal"
          tabIndex={0}
          className="flex-shrink-0"
        />
        <h1
          className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4"
          ref={firstFocusRef}
          tabIndex={-1}
          id="modal-title"
          onBlur={(e) => {
            // Remove tabindex after focus to prevent it from being in tab order
            e.currentTarget.removeAttribute("tabindex");
          }}
        >
          {title}
        </h1>
        <div className="flex-shrink-0 w-10"></div>{" "}
        {/* Spacer to balance the layout */}
      </header>

      <div className="py-3 px-6 md:py-6 md:px-14">
        <div className="w-full mx-auto">
          {mesageForLoginModal !== "" && (
            <FadingMessage message={mesageForLoginModal} delay={5000} />
          )}

          <form
            className="flex flex-col gap-6 pb-8 mt-6"
            onSubmit={(e) => {
              e.preventDefault();
              handleCheckEmailContinue(email);
            }}
          >
            <FloatingLabelInput
              ref={emailRef}
              required
              label="Email"
              type="email"
              value={email}
              placeholder="Email"
              onChange={handleEmailChange}
              onClear={() => {
                setEmailValid(false);
                setErrorMessage(null);
                setEmail("");
              }}
              errorMessage={errorMessage}
            />

            <div className="flex justify-center w-full">
              <WideButton disabled={email.length === 0} buttonType="submit">
                {i18n.get("continue")}
              </WideButton>
            </div>

            {isLoading && <Loader />}

            {isLoadingSocial && <Loader />}
          </form>

          <div className="flex flex-col gap-6 pb-8">
            <div>
              <div className="flex items-center justify-center gap-4">
                <hr className="w-full border-border-token-brand-minimal" />
                <span className="title-s-semibold text-fg-neutral-strong">
                  or
                </span>
                <hr className="w-full border-border-token-brand-minimal" />
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <LoginWithButton
                iconSrc={
                  isDark
                    ? "/images/AppleIcon_dark.svg"
                    : "/images/AppleIcon.svg"
                }
                onClick={handleAppleLogin}
                aria-label="Continue with Apple"
              >
                {i18n.get("continueWithApple")}
              </LoginWithButton>

              <LoginWithButton
                iconSrc="/images/GoogleIcon.svg"
                onClick={() => googleLogin()}
                aria-label="Continue with Google"
              >
                {i18n.get("continueWithGoogle")}
              </LoginWithButton>

              <FacebookLoginButton
                appId={facebookLoginClientId}
                callback={handleFacebookResponse}
                buttonText={i18n.get("continueWithFacebook")}
                aria-label="Continue with Facebook"
              />

              {showMicrosoftLogin && (
                <MicrosoftLoginButton
                  clientId={microsoftLoginClientId}
                  tenantId={microsoftLoginTenantId}
                  redirectUri={microsoftLoginRedirectUri}
                  callback={handleMicrosoftResponse}
                  buttonText={i18n.get("continueWithMicrosoft")}
                  aria-label="Continue with Microsoft"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterEmailComponent;
